import { Paper, Text, Table, Badge, Flex, Tooltip, Box } from "@mantine/core";
import { IconClock, IconActivity } from "@tabler/icons-react";

const STAGE_ORDER = ["NEW", "CONTACTED", "IN_PROGRESS", "WAITING_PAYMENT"];

const STAGE_LABELS = {
    NEW: "Nuevos",
    CONTACTED: "Contactados",
    IN_PROGRESS: "En negociación",
    WAITING_PAYMENT: "Esperando pago",
};

const STAGE_COLORS = {
    NEW: "#4dabf7",
    CONTACTED: "#748ffc",
    IN_PROGRESS: "#f59f00",
    WAITING_PAYMENT: "#ff922b",
};

// Devuelve color según días sin contacto
function activityColor(days) {
    if (days === null || days === undefined) return "gray";
    if (days < 3) return "green";
    if (days < 7) return "yellow";
    return "red";
}

function activityLabel(days) {
    if (days === null || days === undefined) return "—";
    return `${days}d`;
}

// Mini barra de buckets proporcional
function BucketBar({ fresh, warm, stale, cold, total }) {
    if (!total) return <Text size="xs" c="dimmed">—</Text>;
    const pct = (n) => Math.round((n / total) * 100);
    const segments = [
        { value: pct(fresh), color: "var(--mantine-color-green-5)", label: `Frescos: ${fresh}` },
        { value: pct(warm), color: "var(--mantine-color-yellow-4)", label: `Tibios: ${warm}` },
        { value: pct(stale), color: "var(--mantine-color-orange-5)", label: `Viejos: ${stale}` },
        { value: pct(cold), color: "var(--mantine-color-red-5)", label: `Fríos: ${cold}` },
    ].filter((s) => s.value > 0);

    return (
        <Tooltip
            label={
                <Box>
                    <Text size="xs">🟢 Frescos (&lt;7d): {fresh}</Text>
                    <Text size="xs">🟡 Tibios (7-30d): {warm}</Text>
                    <Text size="xs">🟠 Viejos (30-60d): {stale}</Text>
                    <Text size="xs">🔴 Fríos (60d+): {cold}</Text>
                </Box>
            }
            withArrow
        >
            <Flex style={{ height: 14, borderRadius: 4, overflow: "hidden", minWidth: 80 }}>
                {segments.map((s, i) => (
                    <Box
                        key={i}
                        style={{
                            width: `${s.value}%`,
                            backgroundColor: s.color,
                            height: "100%",
                        }}
                    />
                ))}
            </Flex>
        </Tooltip>
    );
}

/**
 * Tabla de aging de leads por etapa activa del embudo.
 * Muestra edad promedio, último contacto promedio y distribución por buckets.
 */
export default function LeadAgingTable({ agingByStage }) {
    if (!agingByStage || agingByStage.length === 0) return null;

    // Ordenar según STAGE_ORDER y excluir etapas no activas
    const rows = STAGE_ORDER
        .map((stage) => agingByStage.find((s) => s._id === stage))
        .filter(Boolean);

    if (rows.length === 0) return null;

    return (
        <Paper shadow="sm" radius="md" p="lg">
            <Flex align="center" gap="sm" mb="md">
                <IconActivity size={18} />
                <Text fw={600} size="lg">Aging del Pipeline</Text>
                <Tooltip
                    label="Muestra cuánto tiempo llevan los leads activos sin avanzar. 'Edad' = desde creación. 'Último contacto' = desde el último intento."
                    withArrow
                    multiline
                    w={300}
                >
                    <Text size="xs" c="dimmed" style={{ cursor: "help" }}>¿Qué es esto?</Text>
                </Tooltip>
            </Flex>

            <div style={{ overflowX: "auto" }}>
                <Table withTableBorder withColumnBorders highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Etapa</Table.Th>
                            <Table.Th ta="center">
                                <Flex align="center" justify="center" gap={4}>
                                    <IconClock size={13} />
                                    <span>Leads</span>
                                </Flex>
                            </Table.Th>
                            <Table.Th ta="center">Edad media</Table.Th>
                            <Table.Th ta="center">Último contacto</Table.Th>
                            <Table.Th ta="center">Distribución de actividad</Table.Th>
                            <Table.Th ta="center">Frescos</Table.Th>
                            <Table.Th ta="center">Tibios</Table.Th>
                            <Table.Th ta="center">Viejos</Table.Th>
                            <Table.Th ta="center">Fríos</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.map((row) => (
                            <Table.Tr key={row._id}>
                                <Table.Td>
                                    <Flex align="center" gap={8}>
                                        <Box
                                            style={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                backgroundColor: STAGE_COLORS[row._id] || "#868e96",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Text size="sm" fw={500}>
                                            {STAGE_LABELS[row._id] || row._id}
                                        </Text>
                                    </Flex>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Text size="sm" fw={600}>{row.count}</Text>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Text size="sm" c="dimmed">
                                        {row.avgDaysSinceCreation != null ? `${row.avgDaysSinceCreation}d` : "—"}
                                    </Text>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Badge
                                        color={activityColor(row.avgDaysSinceActivity)}
                                        variant="light"
                                        size="sm"
                                    >
                                        {activityLabel(row.avgDaysSinceActivity)}
                                    </Badge>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <BucketBar
                                        fresh={row.fresh}
                                        warm={row.warm}
                                        stale={row.stale}
                                        cold={row.cold}
                                        total={row.count}
                                    />
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Text size="xs" c="green.6">{row.fresh}</Text>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Text size="xs" c="yellow.7">{row.warm}</Text>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Text size="xs" c="orange.6">{row.stale}</Text>
                                </Table.Td>
                                <Table.Td ta="center">
                                    <Text size="xs" c="red.6">{row.cold}</Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </div>

            <Text size="xs" c="dimmed" mt="xs">
                Frescos: &lt;7d · Tibios: 7-30d · Viejos: 30-60d · Fríos: 60d+
            </Text>
        </Paper>
    );
}
